import type { UpsertQuoteDetailBody } from '@/lib/types'
import { isAxiosError } from 'axios'
import { dynamicsApi } from './client'
import { sanitizeDataverseRequestBody } from './dataverse-write-contract'
import { assertQuoteEditable } from './quote-editability'
import { withNoChargePricingCleared } from '@/lib/no-charge-line-policy'
import type { UpsertCPQFieldsBody } from './quote-details'

export type CreateQuoteDetailBody = {
  productName: string
  quoteDetailBody: UpsertQuoteDetailBody
  cpqFieldsBody: UpsertCPQFieldsBody
  /** Fixed ids (summary rows): a second create of the same row conflicts instead of adding one. */
  ids?: { cpqFieldsId: string; quoteDetailId: string }
}

function batchLine(...lines: string[]) {
  return lines.join('\r\n')
}

function assertQuoteDetailChangesetSucceeded(responseBody: unknown, operationCount: number) {
  const body = String(responseBody)
  const statuses = [...body.matchAll(/HTTP\/1\.1 (\d{3})/g)].map((match) => Number(match[1]))
  if (statuses.length !== operationCount || statuses.some((status) => status < 200 || status >= 300)) {
    const rawDetail = /"message"\s*:\s*"((?:[^"\\]|\\.)*)"/.exec(body)?.[1]
    const detail = rawDetail ? ` ${JSON.parse(`"${rawDetail}"`)}` : ''
    throw new Error(`Atomic quote line create failed. Dataverse statuses: ${statuses.join(', ') || 'missing'}.${detail}`)
  }
}

const AMBIGUOUS_CREATE_READBACK_DELAYS_MS = [0, 250, 500, 1_000, 2_000]

export class QuoteDetailCreateUnresolvedError extends Error {
  readonly cpqFieldsId: string
  readonly quoteDetailId: string

  constructor(cpqFieldsId: string, quoteDetailId: string) {
    super('Quote line create outcome is unresolved. Refresh the quote before trying again.')
    this.name = 'QuoteDetailCreateUnresolvedError'
    this.cpqFieldsId = cpqFieldsId
    this.quoteDetailId = quoteDetailId
  }
}

function isKnownNonCommit(error: unknown) {
  return Boolean(
    isAxiosError(error)
      && error.response
      && error.response.status >= 400
      && error.response.status < 500
      && error.response.status !== 408
  )
}

async function quoteDetailExists(quoteDetailId: string) {
  try {
    const response = await dynamicsApi.get<{ quotedetailid: string }>(
      `/quotedetails(${quoteDetailId})?$select=quotedetailid`
    )
    return response.data.quotedetailid.toLowerCase() === quoteDetailId.toLowerCase()
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) return false
    throw error
  }
}

async function recoverCommittedQuoteDetail(
  quoteDetailId: string,
  delaysMs: readonly number[] = [0]
) {
  for (const delayMs of delaysMs) {
    if (delayMs > 0) await new Promise((resolve) => setTimeout(resolve, delayMs))
    if (await quoteDetailExists(quoteDetailId)) return true
  }
  return false
}

export async function createQuoteDetail(
  { cpqFieldsBody, quoteDetailBody, productName, ids }: CreateQuoteDetailBody,
  bypassQuoteStateCheck = false
) {
  assertQuoteEditable(bypassQuoteStateCheck)

  const batchBoundary = `batch_${crypto.randomUUID().replaceAll('-', '')}`
  const changesetBoundary = `changeset_${crypto.randomUUID().replaceAll('-', '')}`
  const cpqFieldsId = ids?.cpqFieldsId ?? crypto.randomUUID()
  const quoteDetailId = ids?.quoteDetailId ?? crypto.randomUUID()
  const odataBaseUrl = dynamicsApi.defaults.baseURL
  if (!odataBaseUrl) throw new Error('Dynamics API base URL is missing.')
  const safeCPQFieldsBody = sanitizeDataverseRequestBody('/dsy_lineitemcpqfieldses', cpqFieldsBody)
  const safeQuoteDetailBody = withNoChargePricingCleared(quoteDetailBody, productName)
  // Dataverse stamps the product's name over quotedetailname on create but
  // honours an update in the same changeset, so a line named for itself (a
  // package header) is renamed atomically with its create.
  const ownName = safeQuoteDetailBody.quotedetailname
  const renameOperation =
    ownName && ownName !== productName
      ? [
          `--${changesetBoundary}`,
          'Content-Type: application/http',
          'Content-Transfer-Encoding: binary',
          'Content-ID: 3',
          '',
          'PATCH $2 HTTP/1.1',
          'Content-Type: application/json;type=entry',
          '',
          JSON.stringify({ quotedetailname: ownName }),
        ]
      : []
  const operationCount = renameOperation.length ? 3 : 2
  const batchBody = batchLine(
    `--${batchBoundary}`,
    `Content-Type: multipart/mixed; boundary=${changesetBoundary}`,
    '',
    `--${changesetBoundary}`,
    'Content-Type: application/http',
    'Content-Transfer-Encoding: binary',
    'Content-ID: 1',
    '',
    `POST ${odataBaseUrl}/dsy_lineitemcpqfieldses HTTP/1.1`,
    'Content-Type: application/json;type=entry',
    '',
    JSON.stringify({ ...safeCPQFieldsBody, dsy_lineitemcpqfieldsid: cpqFieldsId }),
    `--${changesetBoundary}`,
    'Content-Type: application/http',
    'Content-Transfer-Encoding: binary',
    'Content-ID: 2',
    '',
    `POST ${odataBaseUrl}/quotedetails HTTP/1.1`,
    'Content-Type: application/json;type=entry',
    '',
    JSON.stringify({
      ...safeQuoteDetailBody,
      quotedetailid: quoteDetailId,
      'dsy_cpqfields@odata.bind': '$1',
    }),
    ...renameOperation,
    `--${changesetBoundary}--`,
    `--${batchBoundary}--`,
    ''
  )

  const requestConfig = {
    headers: {
      Accept: 'application/json',
      'Content-Type': `multipart/mixed; boundary=${batchBoundary}`,
      'OData-MaxVersion': '4.0',
      'OData-Version': '4.0',
    },
    transformResponse: [(data: unknown) => data],
  }
  const submit = () => dynamicsApi.post<string>('/$batch', batchBody, requestConfig)

  let response
  try {
    response = await submit()
  } catch (firstError) {
    if (isKnownNonCommit(firstError)) throw firstError
    if (await recoverCommittedQuoteDetail(quoteDetailId)) return quoteDetailId

    try {
      // Same IDs + same atomic payload make this retry idempotent. If the first
      // request commits late, the retry conflicts instead of creating a second
      // line; exact readback below resolves that conflict as success.
      const retryResponse = await submit()
      assertQuoteDetailChangesetSucceeded(retryResponse.data, operationCount)
      return quoteDetailId
    } catch {
      if (await recoverCommittedQuoteDetail(quoteDetailId, AMBIGUOUS_CREATE_READBACK_DELAYS_MS)) {
        return quoteDetailId
      }
      throw new QuoteDetailCreateUnresolvedError(cpqFieldsId, quoteDetailId)
    }
  }
  assertQuoteDetailChangesetSucceeded(response.data, operationCount)
  return quoteDetailId
}
